import { useState } from "react";
import { Building2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button, Input, Label } from "@bookdental/ui";
import { BRAZILIAN_STATES, type OrganizationType } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { fetchAddressFromCep, formatCep } from "@/lib/viacep";
import { getVisibleOrgTypeOptions } from "@/components/onboarding/constants";
import { OrganizationTypeGrid } from "@/components/organization/OrganizationTypeGrid";
import { CopiarDeOutra } from "@/components/organization/CopiarDeOutra";
import { EspecialidadesDaNovaClinica } from "@/components/organization/EspecialidadesDaNovaClinica";
import { formatCnpj } from "@/lib/cnpj";

// organization.create sempre cria organização de equipe (isPersonal: false) — DENTAL_OFFICE
// aparece como "Clínica Odontológica", não "Cirurgião Dentista".
const visibleTypeOptions = getVisibleOrgTypeOptions(false);

type AddressValues = {
  zipCode: string;
  address: string;
  addressNumber: string;
  addressComplement: string;
  neighborhood: string;
  city: string;
  state: string;
};

const EMPTY_ADDRESS: AddressValues = {
  zipCode: "",
  address: "",
  addressNumber: "",
  addressComplement: "",
  neighborhood: "",
  city: "",
  state: "",
};

type Props = {
  onSuccess: () => void;
  onCancel: () => void;
};

/**
 * Cria organização DE EQUIPE (o backend nunca cria a pessoal por aqui — essa
 * nasce sozinha ao completar o perfil).
 */
export function CreateOrganizationForm({ onSuccess, onCancel }: Props) {
  const [name, setName] = useState("");
  const [types, setTypes] = useState<OrganizationType[]>([]);
  const [cnpj, setCnpj] = useState("");
  const [address, setAddress] = useState<AddressValues>(EMPTY_ADDRESS);
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [copiarDe, setCopiarDe] = useState<string | null>(null);
  const [especialidadeIds, setEspecialidadeIds] = useState<string[]>([]);
  // Clínica do zero escolhe as especialidades aqui; copiando, elas vêm da origem.
  const comEspecialidades = types.includes("DENTAL_OFFICE") && !copiarDe;

  const toggleType = (value: OrganizationType) =>
    setTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );

  const utils = trpc.useUtils();
  const listMineQuery = trpc.organization.listMine.useQuery();

  const create = trpc.organization.create.useMutation({
    onSuccess: () => {
      utils.organization.listMine.invalidate();
      utils.painelDasOrganizacoes.invalidate();
      utils.me.current.invalidate();
      toast.success("Organização criada.");
      onSuccess();
      // `organization.create` já deixa a nova como ativa. Recarrega direto no Início: a rota onde
      // a pessoa estava era da organização antiga, e a nova nasce vazia de qualquer forma.
      window.location.assign("/inicio");
    },
    onError: (err) => toast.error(err.message || "Erro ao criar organização."),
  });

  const handleCepSearch = async () => {
    setNotFound(false);
    const digits = address.zipCode.replace(/\D/g, "");
    if (digits.length !== 8) return;
    setSearching(true);
    const result = await fetchAddressFromCep(digits);
    setSearching(false);
    if (!result) {
      setNotFound(true);
      return;
    }
    setAddress((v) => ({
      ...v,
      zipCode: result.zipCode,
      address: result.address || v.address,
      neighborhood: result.neighborhood || v.neighborhood,
      city: result.city || v.city,
      state: result.state || v.state,
    }));
  };

  // Endereço é obrigatório na criação — só o complemento fica de fora.
  const enderecoCompleto =
    address.zipCode.replace(/\D/g, "").length === 8 &&
    address.address.trim().length >= 2 &&
    address.addressNumber.trim().length >= 1 &&
    address.neighborhood.trim().length >= 2 &&
    address.city.trim().length >= 2 &&
    !!address.state;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!enderecoCompleto) return;
    create.mutate({
      name: name.trim(),
      types,
      copiarDe: copiarDe ?? undefined,
      especialidadeIds: comEspecialidades ? especialidadeIds : [],
      document: cnpj || undefined,
      address: {
        zipCode: address.zipCode,
        address: address.address.trim(),
        addressNumber: address.addressNumber.trim(),
        addressComplement: address.addressComplement.trim() || undefined,
        neighborhood: address.neighborhood.trim(),
        city: address.city.trim(),
        state: address.state as never,
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="createorg-name">Nome da organização</Label>
        <Input
          id="createorg-name"
          placeholder="Ex: Clínica Odonto Sorriso"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          minLength={2}
          autoFocus
        />
      </div>

      <div className="space-y-1.5">
        <Label>Área de atuação</Label>
        <OrganizationTypeGrid
          options={visibleTypeOptions}
          selected={types}
          onToggle={toggleType}
        />
      </div>

      <CopiarDeOutra types={types} minhas={listMineQuery.data ?? []} valor={copiarDe} onChange={setCopiarDe} />

      {comEspecialidades && <EspecialidadesDaNovaClinica value={especialidadeIds} onChange={setEspecialidadeIds} />}

      <div className="space-y-1.5">
        <Label htmlFor="createorg-cnpj">CNPJ (opcional)</Label>
        <Input
          id="createorg-cnpj"
          placeholder="00.000.000/0000-00"
          inputMode="numeric"
          value={formatCnpj(cnpj)}
          onChange={(e) => setCnpj(e.target.value.replace(/\D/g, "").slice(0, 14))}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Endereço</Label>
        <div className="space-y-3 rounded-lg border border-zinc-100 bg-zinc-50/50 p-3 dark:border-border dark:bg-muted/20">
          <div className="space-y-1.5">
            <Label htmlFor="createorg-cep">CEP *</Label>
            <div className="flex gap-2">
              <Input
                id="createorg-cep"
                placeholder="01310-100"
                inputMode="numeric"
                value={formatCep(address.zipCode)}
                onChange={(e) => {
                  setNotFound(false);
                  setAddress((v) => ({ ...v, zipCode: e.target.value.replace(/\D/g, "") }));
                }}
                onBlur={handleCepSearch}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleCepSearch}
                disabled={searching || address.zipCode.replace(/\D/g, "").length !== 8}
              >
                <Search className="h-4 w-4" />
                {searching ? "Buscando..." : "Buscar"}
              </Button>
            </div>
            {notFound && (
              <p className="text-xs text-amber-700">CEP não encontrado. Preencha manualmente.</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px]">
            <div className="space-y-1.5">
              <Label htmlFor="createorg-address">Logradouro *</Label>
              <Input
                id="createorg-address"
                value={address.address}
                onChange={(e) => setAddress((v) => ({ ...v, address: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="createorg-number">Número *</Label>
              <Input
                id="createorg-number"
                value={address.addressNumber}
                onChange={(e) => setAddress((v) => ({ ...v, addressNumber: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="createorg-complement">Complemento (opcional)</Label>
            <Input
              id="createorg-complement"
              placeholder="Sala, andar, bloco..."
              value={address.addressComplement}
              onChange={(e) => setAddress((v) => ({ ...v, addressComplement: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="createorg-neighborhood">Bairro *</Label>
              <Input
                id="createorg-neighborhood"
                value={address.neighborhood}
                onChange={(e) => setAddress((v) => ({ ...v, neighborhood: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="createorg-city">Cidade *</Label>
              <Input
                id="createorg-city"
                value={address.city}
                onChange={(e) => setAddress((v) => ({ ...v, city: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="createorg-state">Estado (UF) *</Label>
            <select
              id="createorg-state"
              value={address.state}
              onChange={(e) => setAddress((v) => ({ ...v, state: e.target.value }))}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">—</option>
              {BRAZILIAN_STATES.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={create.isPending || name.trim().length < 2 || types.length === 0 || !enderecoCompleto}
        >
          {create.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Building2 className="h-4 w-4" />
          )}
          Criar organização
        </Button>
      </div>
    </form>
  );
}
